import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';
import { cartClient } from '@theme/cart-client';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('click', this.#closeSubscriptionDetailsOnClickOutside);

    this.#scheduleDirtySubscriptionGiftHeal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('click', this.#closeSubscriptionDetailsOnClickOutside);
  }

  #closeSubscriptionDetailsOnClickOutside = (event) => {
    this.querySelectorAll('.cart-item__subscription-details[open]').forEach((details) => {
      if (!details.contains(/** @type {Node} */ (event.target))) {
        /** @type {HTMLDetailsElement} */ (details).open = false;
      }
    });
  };

  #scheduleDirtySubscriptionGiftHeal() {
    queueMicrotask(async () => {
      try {
        const correctionPlan = JSON.parse(this.dataset.buyAgainCorrection || 'null');
        const correctedCart = await cartClient.correctBuyAgainCart(this.sectionId, correctionPlan);
        if (!correctedCart && this.querySelector('[data-cart-correction-loading]')) {
          await sectionRenderer.renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' });
          this.#setDrawerEmptyState(!this.querySelector('.cart-form'));
        }
        await cartClient.healDirtySubscriptionGiftLines(this.sectionId);
      } catch (error) {
        console.error('Cart correction error:', error);
      }
    });
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows.find((row) => Number(row.dataset.lineIndex) === line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows.find((row) => Number(row.dataset.lineIndex) === line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
        this.#setDrawerEmptyState(true);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
        this.#syncCartTotalDisplay(parsedResponseText);

        if (this.isDrawer) {
          this.#setDrawerEmptyState(newCartItemCount === 0);
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Changes the selling plan for a cart line item.
   * Uses /cart/change.js with selling_plan so the line item is updated in place,
   * preserving bundle parent-child relationships.
   * Triggered by subscription option buttons inside the cart item rows.
   * @param {Event} event - The click event on a subscription option button.
   */
  changeSellingPlan = async (event) => {
    const button = /** @type {HTMLElement | null} */ (
      event.target instanceof Element && event.target.closest('[data-selling-plan-id]')
    );
    if (!button) return;

    const sellingPlanId = button.dataset.sellingPlanId;
    const lineIndex = parseInt(button.dataset.lineIndex || '0', 10);
    const variantId = parseInt(button.dataset.variantId || '0', 10);

    if (!sellingPlanId || !lineIndex) return;

    // Close the dropdown immediately
    const details = /** @type {HTMLDetailsElement | null} */ (button.closest('details'));
    if (details) details.open = false;

    this.#disableCartItems();

    try {
      const result = await cartClient.changeSellingPlan({
        line: button.dataset.itemKey || lineIndex,
        lineIndex,
        variantId,
        sellingPlanId: Number(sellingPlanId),
        sourceId: this.sectionId,
      });
      document.dispatchEvent(new CustomEvent('tracking:product-interest', {
        detail: {
          source: 'change_purchase_plan',
          cart: result.cart,
          variantId,
          sellingPlanId: Number(sellingPlanId),
        },
      }));
    } catch (error) {
      console.error('changeSellingPlan error:', error);
    } finally {
      this.#enableCartItems();
    }
  };

  /**
   * Removes the selling plan from a cart line item (converts to one-time purchase).
   * Uses /cart/change.js with selling_plan: "" so the line item is updated in place,
   * preserving bundle parent-child relationships.
   * @param {Event} event - The click event on the "Acquisto singolo" option button.
   */
  clearSellingPlan = async (event) => {
    const button = /** @type {HTMLElement | null} */ (
      event.target instanceof Element && event.target.closest('[data-line-index]')
    );
    if (!button) return;

    const lineIndex = parseInt(button.dataset.lineIndex || '0', 10);
    const variantId = parseInt(button.dataset.variantId || '0', 10);
    const quantity = parseInt(button.dataset.quantity || '1', 10);

    if (!lineIndex) return;

    // Close the dropdown immediately
    const details = /** @type {HTMLDetailsElement | null} */ (button.closest('details'));
    if (details) details.open = false;

    this.#disableCartItems();

    try {
      const result = await cartClient.changeSellingPlan({
        line: button.dataset.itemKey || lineIndex,
        lineIndex,
        variantId,
        quantity,
        sellingPlanId: null,
        sourceId: this.sectionId,
      });
      document.dispatchEvent(new CustomEvent('tracking:product-interest', {
        detail: {
          source: 'change_purchase_plan',
          cart: result.cart,
          variantId,
          sellingPlanId: null,
        },
      }));
    } catch (error) {
      console.error('clearSellingPlan error:', error);
    } finally {
      this.#enableCartItems();
    }
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Sums Wrapped gift-wrap surcharges for checked drawer checkboxes.
   * @returns {number}
   */
  #getActiveGiftWrapSurchargeCents() {
    const wrapper = this.querySelector('.cart-items__wrapper[data-money-format]');
    const currency = wrapper?.getAttribute('data-currency') || 'EUR';
    let surcharge = 0;

    this.querySelectorAll('.wrapped-gift-anchor').forEach((anchor) => {
      const checkbox = anchor.querySelector('input[type="checkbox"]');
      if (!checkbox?.checked) return;

      const pricing = anchor.querySelector('[part="pricing"]');
      const rawText = pricing?.textContent?.replace(/\u00a0/g, ' ').trim();
      if (!rawText) return;

      const minorUnits = convertMoneyToMinorUnits(rawText, currency);
      if (minorUnits != null) surcharge += minorUnits;
    });

    return surcharge;
  }

  /**
   * Sums gift-wrap surcharges Wrapped already baked into cart.total_price via its
   * Cart Transform. cart-products.liquid exposes the per-line baked amount as the
   * difference between final_line_price and the gift-free catalog price.
   * @returns {number}
   */
  #getBakedGiftWrapSurchargeCents() {
    let baked = 0;

    this.querySelectorAll('[data-gift-baked-cents]').forEach((cell) => {
      const cents = parseInt(cell.getAttribute('data-gift-baked-cents') || '0', 10);
      if (Number.isFinite(cents) && cents > 0) baked += cents;
    });

    return baked;
  }

  /**
   * Displays the gift-wrap surcharge exactly once.
   *
   * Wrapped's Cart Transform sometimes bakes the surcharge into cart.total_price and the
   * line's final_line_price, and sometimes doesn't (depending on line position/timing). We
   * normalize by starting from Shopify's total, adding the surcharge for every checked gift
   * option, then subtracting whatever Shopify already baked in — so the total is consistent
   * regardless of Wrapped's behavior, and the line items always show the product-only price.
   * @param {{ total_price?: number }} cart
   * @returns {number | undefined}
   */
  #computeDisplayCartTotal(cart) {
    if (cart?.total_price == null) return undefined;

    const surcharge = this.#getActiveGiftWrapSurchargeCents();
    const baked = this.#getBakedGiftWrapSurchargeCents();

    return cart.total_price + surcharge - baked;
  }

  /**
   * Keeps the cart total text-component in sync with cart JSON and Wrapped surcharges.
   * @param {{ total_price?: number; items?: Array<{ final_line_price?: number }> } | undefined} cart
   */
  #syncCartTotalDisplay(cart) {
    const totalPriceCents = this.#computeDisplayCartTotal(cart ?? {});
    if (totalPriceCents == null) return;

    const cartTotal =
      this.refs.cartTotal ?? this.querySelector('[data-testid="cart-total-value"]');
    if (!(cartTotal instanceof HTMLElement)) return;

    const wrapper = this.querySelector('.cart-items__wrapper[data-money-format]');
    const format = wrapper?.getAttribute('data-money-format') || '{{amount}}';
    const currency = wrapper?.getAttribute('data-currency') || 'EUR';
    const formatted = formatMoney(totalPriceCents, format, currency);

    cartTotal.setAttribute('value', formatted);
    cartTotal.textContent = formatted;
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
        this.#scheduleDirtySubscriptionGiftHeal();
      });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      const morphMode =
        event.detail.data?.morphMode ?? (this.isDrawer ? 'hydration' : 'full');
      morphSection(this.sectionId, cartItemsHtml, { mode: morphMode });
      this.#syncCartTotalDisplay(event.detail.resource);

      if (this.isDrawer) {
        const itemCount = event.detail.data?.itemCount;
        this.#setDrawerEmptyState(
          itemCount !== undefined ? itemCount === 0 : !this.querySelector('.cart-form')
        );
      }

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#scheduleDirtySubscriptionGiftHeal();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
        this.#scheduleDirtySubscriptionGiftHeal();
      });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Toggles drawer empty-state classes and labelling on the dialog shell.
   * @param {boolean} isEmpty
   */
  #setDrawerEmptyState(isEmpty) {
    if (!this.isDrawer) return;
    if (this.querySelector('[data-cart-correction-loading]')) isEmpty = false;

    const dialog = this.closest('cart-drawer-component')?.querySelector('.cart-drawer__dialog');
    if (!(dialog instanceof HTMLElement)) return;

    dialog.classList.toggle('cart-drawer--empty', isEmpty);
    dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
